import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { useLogin } from '../../store';
import {
	CountryCode,
	LoginCredsState,
	LoginMaskPhoneCredsState,
	LoginStep,
	MobileLoginLinkResponseState,
} from '../../store/states';
import { emailRegex } from 'utils';
import { useNotification, useTimer } from 'hooks';
import {
	ApprovalChannel,
	ApprovalChannelKind,
	maskEmail,
} from '../approval-channel';

import './mobile-link-sent.scss';

interface IProps {
	props: any;
}

/**
 * Step MOBILE_CODE — the approval-link chooser, reached straight from the
 * credentials step. (Choose Method, which used to sit between the two, left
 * the flow on 2026-09-15; Back and a rejected approval return to CREDS.)
 *
 * Nothing is sent before landing here; this screen
 * asks which channel to use, sends ONLY that one (`channel` on the request),
 * and then polls for the approval exactly as it always did. The polling,
 * the resend schedule (20 s → 40 s → 60 s → support) and the Intercom
 * fallback are the pre-existing logic, unchanged.
 */
export const MobileLinkSent: FC<IProps> = ({ props }) => {
	const country = useRecoilValue(CountryCode);
	const [{ verificationId }, setSendLinkResponse] = useRecoilState(
		MobileLoginLinkResponseState
	);
	const setStep = useSetRecoilState(LoginStep);
	const maskPhoneCreds = useRecoilValue(LoginMaskPhoneCredsState);
	const phone = useRecoilValue(LoginCredsState);
	const [checkStatus, setCheckStatus] = useState(false);
	const [count, setCount] = useState(1);
	// the channel that has been sent (null while choosing) and the one in flight
	const [sentChannel, setSentChannel] = useState<ApprovalChannelKind | null>(
		null
	);
	const [busy, setBusy] = useState<ApprovalChannelKind | null>(null);

	const { loginWithEmail: loginStatus } = useLogin();
	const { successNotification, errorNotification } = useNotification();
	const { onSuccess, onFailed } = props;
	const { seconds, setSeconds } = useTimer();
	// The poll answered status 'expired': the link is spent. Stop polling and show
	// the chooser again, rather than leaving a card that reads "sent" while the
	// backend refuses it. Previously this arrived as a 400 that useNetwork toasted
	// on every 5 s tick.
	const [expired, setExpired] = useState(false);

	/* Back goes to VERIFY (Choose Method), not CREDS. While Choose Method was
	 * out of the flow (2026-09-15 to 2026-09-16) there was nothing between
	 * this screen and credentials, so Back had to skip all the way there.
	 * With the passkey path restored, Back belongs one step up — otherwise
	 * someone who opened the approval chooser has to re-enter credentials
	 * just to reach Device Biometric. */
	const handleClickBtn = useCallback(() => {
		setStep('VERIFY');
	}, [setStep]);

	const getStatus = useCallback(async () => {
		const payload = {
			type: 'getTokenWithCode',
			verificationId,
			// securityCode,
			// code,
		};
		const response = await loginStatus(payload);
		if (response) {
			const { status, token, mfaEnabled, refreshToken } = response ?? {};
			if (status === 'approved') {
				onSuccess({
					token,
					status,
					message: 'Logged in successfully',
					type: 'mobileCode',
					mfaEnabled: mfaEnabled ?? false,
					refreshToken,
				});
				return;
			}
			if (status === 'expired') {
				setExpired(true);
				return;
			}
			if (status === 'rejected') {
				onFailed({
					status: false,
					message: 'Verification failed due to invalid selection',
					type: 'mobileCode',
				});
				/* Back to Choose Method, not credentials: a rejected approval
				 * says this channel failed, not that the account is wrong.
				 * Returning to VERIFY lets the person pick the passkey (or the
				 * other channel) without retyping anything. */
				setStep('VERIFY');
				return;
			}
		}
	}, [loginStatus, onFailed, onSuccess, setStep, verificationId]);

	const isResendAllowed = useMemo(() => !seconds, [seconds]);

	useEffect(() => {
		// Once expired there is nothing left to poll for — the record is gone.
		if (expired) return;
		if (verificationId !== '') {
			getStatus();
		}
		const id = setTimeout(() => {
			setCheckStatus(prev => !prev);
		}, 5000);
		return () => clearTimeout(id);
		// eslint-disable-next-line
	}, [checkStatus, verificationId, expired]);

	// The request Choose Method used to send, plus the channel the person
	// picked. `channel` is the one field this screen adds — see
	// _design/specs/approval-channel-chooser.md for the contract.
	const buildPayload = useCallback(
		(channel: ApprovalChannelKind) => {
			if (emailRegex.test(phone)) {
				return { type: 'maskPhone', email: phone, channel };
			}
			return { type: 'phone', countryCode: country, phone, channel };
		},
		[country, phone]
	);

	// Initial send, or a switch to the other channel. Starts the same 20 s
	// countdown the old screen started on mount.
	const sendVia = useCallback(
		async (channel: ApprovalChannelKind) => {
			if (busy) return;
			setBusy(channel);
			try {
				const response = await loginStatus(buildPayload(channel));
				if (!response) return;
				const { success, verificationId: id, message, errorData } = response;
				if (success && id) {
					setExpired(false);
					setSendLinkResponse({ verificationId: id });
					// name what the server says it sent when it says so; else what was asked for
					const used: ApprovalChannelKind =
						response.channel === 'email' || response.channel === 'phone'
							? response.channel
							: channel;
					setSentChannel(used);
					setSeconds(20);
					successNotification('Link sent successfully');
				} else {
					errorNotification(message ?? errorData?.message);
				}
			} finally {
				setBusy(null);
			}
		},
		[
			busy,
			buildPayload,
			errorNotification,
			loginStatus,
			setSendLinkResponse,
			setSeconds,
			successNotification,
		]
	);

	const handleResendOTP = useCallback(async () => {
		if (!isResendAllowed || count > 3 || !sentChannel || busy) return;

		setCount(prev => prev + 1);
		if (count === 1) {
			setSeconds(40);
		} else if (count === 2) {
			setSeconds(60);
		}
		setBusy(sentChannel);
		try {
			const response = await loginStatus(buildPayload(sentChannel));
			if (response) {
				const { success, verificationId: id, message, errorData } = response;
				if (success && id) {
					setSendLinkResponse({ verificationId: id });
					successNotification(message);
				} else {
					errorNotification(message ?? errorData.message);
				}
			}
		} finally {
			setBusy(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		buildPayload,
		busy,
		count,
		loginStatus,
		isResendAllowed,
		sentChannel,
		setStep,
	]);

	const onClickIntercom = () => {
		(window as any)?.Intercom?.(
			'showNewMessage',
			'Unable to Login, Not receiving message'
		);
	};

	const emailDisplay = useMemo(
		() => (emailRegex.test(phone) ? maskEmail(phone) : undefined),
		[phone]
	);
	const identity = emailDisplay ?? `${country} ${phone}`.trim();
	const phoneDisplay = `${country} ${maskPhoneCreds}`.trim();

	// FYI please do not remove the commented code they are super important
	// (the securityCode block that used to render beside the spinner):
	// {/* <div className="mobile-link__number">{securityCode}</div>
	//   <div className="mobile-link__specification">
	//     Tap <strong>{securityCode}</strong> on your phone to verify your
	//     phone number{" "}
	//   </div> */}
	return (
		<div className="mobile-link">
			<div className="mobile-link__wrapper mobile-link__wrapper--sv2">
				<ApprovalChannel
					purpose="signin"
					identity={identity}
					phoneDisplay={phoneDisplay}
					emailDisplay={emailDisplay}
					sent={sentChannel}
					expired={expired}
					busy={busy}
					resend={{
						allowed: isResendAllowed,
						seconds,
						exhausted: count === 4,
					}}
					onSend={sendVia}
					onResend={handleResendOTP}
					onBack={handleClickBtn}
					onSupport={onClickIntercom}
				/>
			</div>
		</div>
	);
};
